import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';

const ContactSection = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: [
        { text: companyInfo.phones[0], link: `tel:${companyInfo.phones[0]}` },
        { text: companyInfo.phones[3], link: `tel:${companyInfo.phones[3]}` }
      ],
      isClickable: true
    },
    {
      icon: Mail,
      title: 'Email',
      details: [
        { text: companyInfo.email, link: `mailto:${companyInfo.email}` }
      ],
      isClickable: true
    },
    {
      icon: MapPin,
      title: 'Address',
      details: [
        { text: companyInfo.address, link: `https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}` }
      ],
      isClickable: true
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: [
        { text: companyInfo.timings.weekdays },
        { text: companyInfo.timings.sunday }
      ],
      isClickable: false
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-[#007897] font-semibold text-sm uppercase tracking-wider">Contact Us</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
            Get in Touch
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Have questions or need assistance? Reach out to us through phone or WhatsApp.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-4">
            {contactInfo.map((item, index) => (
              <div
                key={`section-item-${index}`}
                className="group p-6 bg-gray-50 rounded-xl hover:bg-[#007897] transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#007897]/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                    <item.icon className="w-6 h-6 text-[#007897] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors">
                      {item.title}
                    </h3>
                    {item.details.map((detail, i) => (
                      item.isClickable && detail.link ? (
                        <a 
                          key={i} 
                          href={detail.link}
                          target={detail.link.startsWith('http') ? '_blank' : '_self'}
                          rel={detail.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="block text-sm text-gray-600 group-hover:text-blue-100 transition-colors hover:underline"
                        >
                          {detail.text}
                        </a>
                      ) : (
                        <p key={i} className="text-sm text-gray-600 group-hover:text-blue-100 transition-colors">
                          {detail.text || detail}
                        </p>
                      )
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Info Display */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-2xl p-8 h-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h3>
              <p className="text-gray-600 mb-8">
                Our team is available during working hours to assist you with all your diagnostic needs.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {/* Phone Info - Clickable */}
                <a 
                  href={`tel:${companyInfo.phones[0]}`}
                  className="flex items-center justify-center gap-3 p-6 bg-[#007897] text-white rounded-xl hover:bg-[#006680] transition-colors cursor-pointer"
                >
                  <Phone size={24} />
                  <div className="text-left">
                    <p className="text-sm text-blue-100">Call Us</p>
                    <p className="font-bold">{companyInfo.phones[0]}</p>
                  </div>
                </a>

                {/* WhatsApp Info - Clickable */}
                <a 
                  href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hello, I would like to inquire about your diagnostic services.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 p-6 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors cursor-pointer"
                >
                  <MessageCircle size={24} />
                  <div className="text-left">
                    <p className="text-sm text-green-100">WhatsApp Us</p>
                    <p className="font-bold">{companyInfo.whatsapp}</p>
                  </div>
                </a>
              </div>

              {/* Additional Info */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">Our Timing</h4>
                <div className="grid sm:grid-cols-2 gap-4 text-gray-600">
                  <div>
                    <p className="text-gray-900 font-medium">Monday - Saturday</p>
                    <p>8:00 AM - 8:00 PM</p>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Sunday</p>
                    <p>8:00 AM - 1:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
